// This mixin is used to communicate with the native app.
// It is used to show the save button, notify the app of validation errors, and pop to the root view.
// The root Vue instance is the one that handles the communication with the native app, this simply provides the interface.
// It is also used to handle the native app ready event.

export const appEmbedMix = {
  props: {
    forAppEmbed: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    // Delegate all public interface methods to root Vue instance
    setupNativeSaveButton(saveCallback) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.setupNativeSaveButton(saveCallback);
    },
    setNativeSaveVisible(visible) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.setNativeSaveVisible(visible);
    },
    notifyAppOfValidationError(errorMessage) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.notifyAppOfValidationError(errorMessage);
    },
    askAppToPop(notification) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.askAppToPop(notification);
    },
    askAppToPush(url, title) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.askAppToPush(url, title);
    },
    askAppToReloadPage() {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.askAppToReloadPage();
    },
    // Post a named notification ({ name, data }) to the native app without
    // navigating, e.g. so it can count a key action.
    notifyApp(notification) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.notifyApp(notification);
    },
    // Ask the native shell to confirm before popping this screen (back button,
    // swipe back, hardware back) while unsaved work exists. App versions that
    // predate the message ignore it and pop immediately, as before.
    setNativeExitConfirmation(enabled, message) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.setNativeExitConfirmation(enabled, message);
    },
    // While the exit guard is on, a native back press asks the embed first
    // (navBack): register a callback that navigates one step back internally.
    // If no listener is registered, the native side falls back to its discard
    // confirmation dialog.
    listenForNativeBack(callback) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.queueNativeListener({ type: 'navBack', callback });
    },
    stopListeningForNativeBack() {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.removeNativeListener('navBack');
    },
    // Native uploads images through its own picker/S3 pipeline and pushes each
    // finished attachment in over the bridge. The callback receives the
    // attachment payload ({ id, name, url }).
    listenForNativeAttachments(callback) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.queueNativeListener({ type: 'attachmentAdded', callback });
    },
    stopListeningForNativeAttachments() {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.removeNativeListener('attachmentAdded');
    },
    // The native formatting toolbar (inputAccessoryView above the keyboard)
    // sends formatting commands over the bridge. The callback receives
    // { attribute, url? } — e.g. { attribute: 'bold' } to toggle bold, or
    // { attribute: 'href', url: '...' } to link the selection.
    listenForNativeFormatCommands(callback) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.queueNativeListener({ type: 'formatCommand', callback });
    },
    stopListeningForNativeFormatCommands() {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.removeNativeListener('formatCommand');
    },
    // Reports which Trix attributes are active at the current cursor/selection
    // so native can highlight its toolbar buttons.
    notifyAppOfComposerFormatState(attributes) {
      if (!this.forAppEmbed) {
        return;
      }
      this.$root.notifyAppOfComposerFormatState(attributes);
    },
  },
};
